import './Terminal.css';

class Terminal {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.isVisible = options.visible || false;
    this.commands = new Map();
    this.currentInputArea = null;
    this.terminalOutput = null;
    
    // Store reference to this component on the container for external access
    container.__terminalComponent = this;
    
    // Bind methods
    this.toggle = this.toggle.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.clear = this.clear.bind(this);
    this.addOutput = this.addOutput.bind(this);
    this.processCommand = this.processCommand.bind(this);
    this.createNewInputLine = this.createNewInputLine.bind(this);
    this.executeCommand = this.executeCommand.bind(this);
    
    // Register built-in commands
    this.registerBuiltInCommands();
    
    // Set initial button state
    this.syncButtonState();

    // Set initial height, especially for mobile
    this.setInitialHeight();
  }
  
  /**
   * Sync the toggle button state with the current visibility
   */
  syncButtonState() {
    const toggleButton = document.getElementById('terminal-toggle');
    if (toggleButton) {
      // Force DOM reflow to ensure visual update happens immediately
      if (this.isVisible) {
        toggleButton.classList.remove('active');
        void toggleButton.offsetWidth; // Force reflow
        toggleButton.classList.add('active');
      } else {
        toggleButton.classList.remove('active');
      }
      
      // For mobile: force immediate style application
      if (!this.isVisible) {
        // Ensure styles are applied immediately
        toggleButton.style.backgroundColor = 'transparent';
        toggleButton.style.color = 'var(--text-color)';
        
        // Remove inline styles after a short delay
        setTimeout(() => {
          toggleButton.style.backgroundColor = '';
          toggleButton.style.color = '';
        }, 100);
      }
    }
  }
  
  /**
   * Render the terminal component
   */
  render() {
    // Add container class
    this.container.className = 'terminal__container';
    
    this.container.innerHTML = `
      <div class="terminal__header-bar">
        <span class="terminal__drag-handle-mobile material-symbols-outlined">drag_handle</span>
        <span class="terminal__title-text">Terminal</span>
        <div class="terminal__controls">
          <span class="terminal__clear-btn terminal__icon" title="Clear Terminal">
            <span class="material-symbols-outlined">ink_eraser</span>
          </span>
          <span class="terminal__close-btn terminal__icon" title="Close Terminal">
            <span class="material-symbols-outlined">close</span>
          </span>
        </div>
      </div>
      <div class="terminal__output"></div>
    `;
    
    this.terminalOutput = this.container.querySelector('.terminal__output');
    
    // Set initial visibility
    this.container.style.display = this.isVisible ? 'flex' : 'none';
    
    // Ensure button state is synced with visibility
    this.syncButtonState();
    
    // Add welcome message and prompt
    this.reset();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Add direct access to the clear button for external scripts
    this.clearButton = this.container.querySelector('.terminal__clear-btn');
    
    // Add a direct method to force reset button state that can be called externally
    this.resetClearButtonState = () => {
      if (this.clearButton) {
        this.clearButton.classList.add('reset-state');
        this.clearButton.style.cssText = 'background-color: transparent !important; color: var(--text-color) !important;';
        
        // Remove after a delay
        setTimeout(() => {
          this.clearButton.classList.remove('reset-state');
          this.clearButton.style.cssText = '';
        }, 500);
      }
    };
    
    // Expose terminal control methods globally
    window.terminalControl = {
      open: this.open,
      close: this.close,
      toggle: this.toggle,
      resetClearButton: this.resetClearButtonState
    };
    
    // If it was initially visible and height wasn't set by persistence, ensure default is applied
    // This can be redundant if setInitialHeight is called in constructor and works as expected
    // but good as a fallback after innerHTML overwrite.
    if (this.isVisible && !localStorage.getItem('app-terminal-height')) {
        this.setInitialHeight(); 
    }
    
    return this;
  }
  
  /**
   * Set up event listeners for the terminal
   */
  setupEventListeners() {
    // Terminal clear button
    const clearButton = this.container.querySelector('.terminal__clear-btn');
    if (clearButton) {
      clearButton.addEventListener('click', (e) => {
        this.reset();
        
        // Force immediate visual state reset
        clearButton.classList.remove('active');
        clearButton.blur();
        
        // For mobile: force immediate style application with high priority
        if (window.matchMedia('(max-width: 768px)').matches) {
          // Direct style manipulation for immediate feedback
          clearButton.style.cssText = "background-color: transparent !important; color: var(--text-color) !important;";
          
          // Force reflow
          void clearButton.offsetWidth;
          
          // Set a flag to indicate this button was clicked
          clearButton.setAttribute('data-just-clicked', 'true');
          
          // Remove the styles and flags after enough time
          setTimeout(() => {
            clearButton.style.cssText = '';
            clearButton.removeAttribute('data-just-clicked');
          }, 800); // Longer timeout to ensure it works
        }
        
        // For mobile: also try the reset with a slight delay
        setTimeout(() => {
          this.resetButtonState(clearButton);
        }, 50);
      });
      
      // Add touchend listener for mobile
      clearButton.addEventListener('touchend', (e) => {
        if (!clearButton.getAttribute('data-just-clicked')) {
          setTimeout(() => {
            // Force state reset
            clearButton.style.cssText = "background-color: transparent !important; color: var(--text-color) !important;";
          }, 10);
        }
      });
    }
    
    // Terminal close button
    const closeButton = this.container.querySelector('.terminal__close-btn');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        this.close();
        
        // Force immediate visual state reset
        closeButton.classList.remove('active');
        closeButton.blur();
        
        // For mobile: force immediate style application
        this.resetButtonState(closeButton);
      });
    }
    
    // Make clicking anywhere in terminal focus the input area
    this.terminalOutput.addEventListener('click', (e) => {
      if (this.currentInputArea && e.target !== this.currentInputArea) {
        this.currentInputArea.focus();
      }
    });

    // Long press and drag to resize terminal on mobile
    const terminalHeaderBar = this.container.querySelector('.terminal__header-bar');
    if (terminalHeaderBar) {
      this.setupMobileTerminalResize(terminalHeaderBar);
    }
  }
  
  /**
   * Reset button visual state immediately
   * @param {HTMLElement} button - The button to reset
   */
  resetButtonState(button) {
    if (!button) return;
    
    // Remove all possible active/hover classes
    button.classList.remove('active', 'hover');
    
    // Force immediate removal of hover/active styles on mobile
    if (window.matchMedia('(max-width: 768px)').matches) {
      // Temporarily apply inline styles for immediate visual feedback
      button.style.backgroundColor = 'transparent';
      button.style.color = 'var(--text-color)';
      
      // Force reflow to ensure styles get applied
      void button.offsetWidth;
      
      // Remove hover/active effect after a short delay
      setTimeout(() => {
        // Create and trigger a "mouseout" event to clear any lingering hover states
        const mouseOut = new MouseEvent('mouseout', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        button.dispatchEvent(mouseOut);
        
        // Remove inline styles
        button.style.backgroundColor = '';
        button.style.color = '';
      }, 50);
      
      // Create and append a temporary overlay to catch any touch events
      // This helps clear persistent hover states on iOS
      const touchOverlay = document.createElement('div');
      touchOverlay.style.position = 'fixed';
      touchOverlay.style.top = '0';
      touchOverlay.style.left = '0';
      touchOverlay.style.width = '100%';
      touchOverlay.style.height = '100%';
      touchOverlay.style.zIndex = '9999';
      touchOverlay.style.background = 'transparent';
      document.body.appendChild(touchOverlay);
      
      // Remove it after a short delay
      setTimeout(() => {
        document.body.removeChild(touchOverlay);
      }, 100);
    }
  }
  
  /**
   * Reset terminal to initial state with welcome message
   */
  reset() {
    this.clear();
    this.addOutput('Welcome to Our Appologies. Type \'help\' to learn commands to navigate.');
    this.createNewInputLine();
  }
  
  /**
   * Register built-in commands
   */
  registerBuiltInCommands() {
    // Help command
    this.registerCommand('help', () => {
      this.addOutput('Available commands:');
      
      const commands = [
        { cmd: 'help', desc: 'Displays this list of commands.' },
        { cmd: 'open <filepath>', desc: 'Opens the specified file in a new tab (e.g., open apps/GreenCheck.app).' },
        { cmd: 'MapsTo <filepath>', desc: "Alias for 'open'. (e.g., MapsTo about/the-team.md)." },
        { cmd: 'clear', desc: 'Clears the terminal screen.' },
        { cmd: 'date / time', desc: 'Displays the current date and time.' },
        { cmd: 'ping', desc: "Check connectivity with our servers... may take a moment to respond." },
        { cmd: 'sudo <command>', desc: 'Try to be a superuser... if you dare.' },
        { cmd: 'url [path]', desc: 'Shows current URL or navigates to specified path (e.g., url /about).' },
        { cmd: 'goto <route>', desc: 'Navigates to a specific route (e.g., goto /team).' },
        { cmd: 'subscribe', desc: 'Join our newsletter – We\'re Not Sorry ⚡️' },
        { cmd: 'goingup', desc: 'Elevates your user experience.' }
      ];
      
      commands.forEach((item) => {
        const cmdLine = document.createElement('div');
        cmdLine.className = 'terminal__line terminal__help-highlight-command';
        cmdLine.textContent = item.cmd;
        this.terminalOutput.appendChild(cmdLine);
        
        const descLine = document.createElement('div');
        descLine.className = 'terminal__line terminal__help-description';
        descLine.textContent = item.desc;
        this.terminalOutput.appendChild(descLine);
      });
    });
    
    // Clear command
    this.registerCommand('clear', () => {
      this.reset();
    });
    
    // Date command
    this.registerCommand('date', () => {
      this.addOutput(new Date().toString());
    });
    
    // Time command - alias for date
    this.registerCommand('time', () => {
      this.addOutput(new Date().toString());
    });
    
    // Ping command - launches the pong game
    this.registerCommand('ping', () => {
      // A brief delay to simulate checking connectivity
      this.addOutput('Checking connectivity...');
      
      setTimeout(() => {
        this.launchPongGame();
      }, 800);
    });
    
    // Sudo command
    this.registerCommand('sudo', () => {
      this.addOutput("ACCESS DENIED. Nice try! This is a frontend simulation.", "error");
    });
    
    // Open command
    this.registerCommand('open', (args) => {
      if (args.trim() === '') {
        this.addOutput('Error: Missing file path for open. Usage: open <filepath>', "error");
      } else {
        // Normalize path by removing any leading slash
        const normalizedPath = args.startsWith('/') ? args.substring(1) : args;
        
        // Special handling for newsletter path
        if (normalizedPath === 'were_not_sorry/README.sub') {
          this.addOutput('root@ourappologies:~$ subscribe.sh');
          
          setTimeout(() => {
            this.addOutput('Opening README.sub...');
            if (window.router) {
              window.router.navigate('/newsletter');
            }
          }, 500);
          return;
        }
        
        this.addOutput(`Navigating to ${normalizedPath}...`);
        this.openFile(normalizedPath);
        
        // URL is now updated inside the openFile method
      }
    });
    
    // MapsTo command - alias for open
    this.registerCommand('mapsto', (args) => {
      if (args.trim() === '') {
        this.addOutput('Error: Missing file path for MapsTo. Usage: MapsTo <filepath>', "error");
      } else {
        // Normalize path by removing any leading slash
        const normalizedPath = args.startsWith('/') ? args.substring(1) : args;
        
        // Special handling for newsletter path
        if (normalizedPath === 'were_not_sorry/README.sub') {
          this.addOutput('root@ourappologies:~$ subscribe.sh');
          
          setTimeout(() => {
            this.addOutput('Opening README.sub...');
            if (window.router) {
              window.router.navigate('/newsletter');
            }
          }, 500);
          return;
        }
        
        this.addOutput(`Navigating to ${normalizedPath}...`);
        this.openFile(normalizedPath);
      }
    });
    
    // URL command to show the current URL or navigate
    this.registerCommand('url', (args) => {
      if (args.trim() === '') {
        // Display current URL
        const currentUrl = window.location.href;
        this.addOutput(`Current URL: ${currentUrl}`);
      } else {
        // Navigate to URL
        this.addOutput(`Navigating to: ${args}`);
        if (window.router) {
          window.router.navigate(args);
        }
      }
    });
    
    // Goto command for direct route navigation
    this.registerCommand('goto', (args) => {
      if (args.trim() === '') {
        this.addOutput('Error: Missing route path. Usage: goto <route>', "error");
      } else {
        this.addOutput(`Navigating to route: ${args}`);
        if (window.router) {
          window.router.navigate(args);
        }
      }
    });
    
    // Subscribe command for newsletter subscription
    this.registerCommand('subscribe', () => {
      this.addOutput('root@ourappologies:~$ subscribe.sh');
      
      // Add a short delay for animation effect
      setTimeout(() => {
        this.addOutput('Opening README.sub...');
        
        // Navigate to newsletter page
        if (window.router) {
          window.router.navigate('/newsletter');
        }
      }, 500);
    });

    // GoingUp command - plays elevator music in the terminal
    this.registerCommand('goingup', () => {
      // A brief delay to simulate loading
      this.addOutput('Elevating your experience...');
      
      setTimeout(() => {
        this.launchMusicPlayer();
      }, 800);
    });
  }
  
  /**
   * Register a custom command
   */
  registerCommand(name, handler) {
    this.commands.set(name.toLowerCase(), handler);
  }
  
  /**
   * Process a command
   */
  processCommand(commandText) {
    const parts = commandText.split(' ');
    const command = parts[0].toLowerCase();
    const args = parts.slice(1).join(' ');
    
    if (this.commands.has(command)) {
      this.commands.get(command)(args);
    } else {
      this.addOutput(`Command not recognized: ${commandText}. Type 'help' for available commands.`, "error");
    }
  }
  
  /**
   * Add output to terminal
   */
  addOutput(text, type) {
    const line = document.createElement('div');
    line.className = 'terminal__line';
    
    if (type === 'error') {
      line.classList.add('terminal__error');
    }
    
    line.textContent = text;
    this.terminalOutput.appendChild(line);
    this.terminalOutput.scrollTop = this.terminalOutput.scrollHeight;
  }
  
  /**
   * Create a new input line
   */
  createNewInputLine() {
    const promptContainer = document.createElement('div');
    promptContainer.className = 'terminal__current-input-line';
    
    const promptSpan = document.createElement('span');
    promptSpan.className = 'terminal__prompt';
    promptSpan.textContent = 'the-0-to-1-machine@Mac ~ %';
    promptContainer.appendChild(promptSpan);
    
    const inputArea = document.createElement('span');
    inputArea.className = 'terminal__input-area';
    inputArea.contentEditable = true;
    inputArea.spellcheck = false;
    promptContainer.appendChild(inputArea);
    
    this.terminalOutput.appendChild(promptContainer);
    
    // Update reference to current input
    this.currentInputArea = inputArea;
    
    // Add event listener for input
    inputArea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        
        const commandText = inputArea.textContent.trim();
        
        // Make current line static
        inputArea.contentEditable = false;
        promptContainer.classList.remove('terminal__current-input-line');
        promptContainer.classList.add('terminal__line');
        
        // Process the command
        if (commandText) {
          this.processCommand(commandText);
        }
        
        // Create a new input line
        this.createNewInputLine();
      }
    });
    
    // Focus the input
    inputArea.focus();
    
    // Scroll to bottom
    this.terminalOutput.scrollTop = this.terminalOutput.scrollHeight;
  }
  
  /**
   * Open a file using custom event
   */
  openFile(filepath) {
    // Check if this is a known file path
    const knownPaths = [
      'about/the-biz.info',
      'about/the-team.md',
      'apps/GreenCheck.app',
      'apps/Monk_Mindset.app',
      'apps/StoryTime.app',
      'apps/components_4_$ale.jsx',
      'ologies/EVERYTHING.json',
      'were_not_sorry/README.sub',
      'dm_us/do-it.sh'
    ];
    
    // Normalize path by removing any leading slash
    const normalizedPath = filepath.startsWith('/') ? filepath.substring(1) : filepath;
    console.log('Terminal: Attempting to open file:', normalizedPath);
    
    if (knownPaths.includes(normalizedPath)) {
      // Dispatch event to handle elsewhere
      const event = new CustomEvent('file:request-open', { 
        detail: { filepath: normalizedPath },
        bubbles: true 
      });
      this.container.dispatchEvent(event);
      
      // Update URL if router is available
      if (window.router && typeof window.router.navigateToFile === 'function') {
        window.router.navigateToFile(normalizedPath);
      }
    } else {
      this.addOutput(`Error: File or path not found: ${filepath}`, "error");
    }
  }
  
  /**
   * Toggle terminal visibility
   */
  toggle() {
    if (this.isVisible) {
      this.close();
    } else {
      this.open();
    }
    return this.isVisible;
  }
  
  /**
   * Open the terminal
   */
  open() {
    this.isVisible = true;
    this.container.style.display = 'flex';
    
    // Focus input only on non-mobile devices
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile && this.currentInputArea) {
      setTimeout(() => {
        this.currentInputArea.focus();
      }, 10);
    }
    
    // Update toggle button state
    this.syncButtonState();
    
    return this;
  }
  
  /**
   * Close the terminal
   */
  close() {
    this.isVisible = false;
    this.container.style.display = 'none';
    
    // Update toggle button state
    this.syncButtonState();
    
    // Enable scrolling in case it was disabled by games
    this.enableScrolling();
    
    return this;
  }
  
  /**
   * Clear the terminal
   */
  clear() {
    if (this.terminalOutput) {
      this.terminalOutput.innerHTML = '';
    }
  }
  
  /**
   * Disable terminal scrolling (for games)
   */
  disableScrolling() {
    if (this.terminalOutput) {
      this.terminalOutput.dataset.originalOverflow = this.terminalOutput.style.overflow;
      this.terminalOutput.style.overflow = 'hidden';
      
      const preventScroll = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      
      this.terminalOutput.addEventListener('wheel', preventScroll, { passive: false });
      this.terminalOutput.addEventListener('touchmove', preventScroll, { passive: false });
      this.preventScrollListener = preventScroll;
    }
  }
  
  /**
   * Enable terminal scrolling
   */
  enableScrolling() {
    if (this.terminalOutput) {
      if (this.terminalOutput.dataset.originalOverflow) {
        this.terminalOutput.style.overflow = this.terminalOutput.dataset.originalOverflow;
      } else {
        this.terminalOutput.style.overflow = 'auto';
      }
      
      if (this.preventScrollListener) {
        this.terminalOutput.removeEventListener('wheel', this.preventScrollListener);
        this.terminalOutput.removeEventListener('touchmove', this.preventScrollListener);
      }
    }
  }
  
  /**
   * Launch the terminal Pong game
   */
  launchPongGame() {
    // Clear some space in the terminal
    this.addOutput('');
    this.addOutput('=== TERMINAL PONG ===');
    this.addOutput('Use ↑ ↓ arrow keys or swipe to move your paddle. First to 5 points wins!');
    
    // Create a simple game container
    const gameContainer = document.createElement('div');
    gameContainer.className = 'terminal__pong-container';
    this.terminalOutput.appendChild(gameContainer);
    
    // For simplicity, we'll import the game code separately
    // This would eventually be a separate component/module
    import('./TerminalPong.js').then(module => {
      const TerminalPong = module.default;
      new TerminalPong(gameContainer, { 
        onGameStart: () => this.disableScrolling(),
        onGameEnd: () => this.enableScrolling()
      }).start();
    }).catch(err => {
      console.error('Error loading Pong game:', err);
      this.addOutput('Error loading Pong game. Try again later.', 'error');
    });
    
    // Add a new input line below the game
    setTimeout(() => {
      this.createNewInputLine();
    }, 100);
  }

  /**
   * Creates a retro music player in the terminal
   */
  launchMusicPlayer() {
    // Clear some space in the terminal
    this.addOutput('');
    this.addOutput('=== ELEVATOR MUSIC ===');
    this.addOutput('Enjoy the ride to the top floor.');
    
    // Create a music player container
    const playerContainer = document.createElement('div');
    playerContainer.className = 'terminal__music-container';
    this.terminalOutput.appendChild(playerContainer);
    
    // For simplicity, we'll import the player code separately
    // just like we did with Pong
    import('./TerminalMusicPlayer.js').then(module => {
      const TerminalMusicPlayer = module.default;
      const player = new TerminalMusicPlayer(playerContainer, {
        onStop: () => {
          // Remove the player container when stopped
          playerContainer.remove();
          
          // Add a new input line
          this.createNewInputLine();
        }
      });
      
      // Initialize and start the player
      player.init().start();
    }).catch(err => {
      console.error('Error loading Music Player:', err);
      this.addOutput('Error loading Music Player. Try again later.', 'error');
    });
    
    // Add a new input line below the player
    setTimeout(() => {
      this.createNewInputLine();
    }, 100);
  }

  /**
   * Sets up touch-based resizing for the terminal on mobile.
   * @param {HTMLElement} headerBar - The terminal header bar element.
   */
  setupMobileTerminalResize(headerBar) {
    let начальнаяТочкаКасанияY = 0; // initialTouchY
    let начальнаяВысотаТерминала = 0; // initialTerminalHeight
    let активноеИзменениеРазмера = false; // isResizingActive
    let таймерДлительногоНажатия = null; // longPressTimer
    const ВРЕМЯ_ДЛИТЕЛЬНОГО_НАЖАТИЯ = 500; // LONG_PRESS_DURATION (ms)

    headerBar.addEventListener('touchstart', (e) => {
      // Only proceed if it's a mobile device (redundant check, but good practice)
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) return;

      // Clear any existing timer
      if (таймерДлительногоНажатия) clearTimeout(таймерДлительногоНажатия);

      // Start a timer for long press
      таймерДлительногоНажатия = setTimeout(() => {
        активноеИзменениеРазмера = true;
        начальнаяТочкаКасанияY = e.touches[0].clientY;
        начальнаяВысотаТерминала = this.container.offsetHeight;
        // Optionally add a class to the header bar for visual feedback
        headerBar.classList.add('terminal-resizing'); 
        // Prevent default touch actions like scrolling while resizing
        // document.body.style.overflow = 'hidden'; // May not be needed due to global overflow:hidden
        console.log('Terminal resize activated by long press');
      }, ВРЕМЯ_ДЛИТЕЛЬНОГО_НАЖАТИЯ);

      // Prevent page scroll when starting touch on header
      // e.preventDefault(); // This might prevent other interactions if not careful
    }, { passive: true }); // Use passive:true for touchstart unless preventDefault is strictly needed early

    document.addEventListener('touchmove', (e) => {
      if (!активноеИзменениеРазмера) return;
      // Prevent page scroll during resize
      e.preventDefault();

      const дельтаY = e.touches[0].clientY - начальнаяТочкаКасанияY;
      let новаяВысота = начальнаяВысотаТерминала - дельтаY; // Subtract because dragging up increases height

      const минимальнаяВысота = 100; // minTerminalHeight
      const максимальнаяВысота = window.innerHeight * 0.8; // maxTerminalHeight (e.g., 80% of viewport)

      новаяВысота = Math.max(минимальнаяВысота, Math.min(новаяВысота, максимальнаяВысота));
      this.container.style.height = `${новаяВысота}px`;
    }, { passive: false }); // Use passive:false for touchmove when preventDefault is used

    document.addEventListener('touchend', (e) => {
      // Clear the long press timer if touch ends before it fires
      if (таймерДлительногоНажатия) clearTimeout(таймерДлительногоНажатия);
      таймерДлительногоНажатия = null;

      if (активноеИзменениеРазмера) {
        активноеИзменениеРазмера = false;
        headerBar.classList.remove('terminal-resizing');
        // document.body.style.overflow = ''; // Restore body scroll if it was changed
        localStorage.setItem('app-terminal-height', this.container.style.height);
        console.log('Terminal resize finished, height saved:', this.container.style.height);
      }
    });
  }

  setInitialHeight() {
    const persistedHeight = localStorage.getItem('app-terminal-height');
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const maxHeight = window.innerHeight * 0.8; // 80% of viewport height as max

    if (persistedHeight) {
      this.container.style.height = persistedHeight;
    } else {
      // Start at maximum height if no persisted value
      this.container.style.height = `${maxHeight}px`;
      console.log(`Terminal height set to maximum: ${maxHeight}px`);
    }
  }

  /**
   * Execute a command programmatically
   * This can be called from other components to run terminal commands
   */
  executeCommand(commandText) {
    // Make sure the terminal is visible
    if (!this.isVisible) {
      this.open();
    }
    
    // Add the command to output as if user typed it
    const commandLine = document.createElement('div');
    commandLine.className = 'terminal__line';
    commandLine.innerHTML = `<span class="terminal__prompt">$</span> ${commandText}`;
    this.terminalOutput.appendChild(commandLine);
    
    // Process the command
    this.processCommand(commandText);
    
    // Scroll to the bottom to show results
    setTimeout(() => {
      this.terminalOutput.scrollTop = this.terminalOutput.scrollHeight;
    }, 50);
    
    return this;
  }
}

export default Terminal; 